import type { BacktestResult } from '../api'

interface BacktestConfigGroupsProps {
  configInput: BacktestResult['configInput']
  formatCurrency?: (n: number | undefined) => string
  className?: string
}

// Keys that already appear in the topbar / drawer basic-params section
// (Tick Data, Market, Range, Timeframe) and so are filtered out of the
// "Backtest Settings" group to avoid duplication.
const BACKTEST_CONFIG_DEDUP_KEYS = new Set([
  'symbol',
  'timeframe',
  'start_date',
  'end_date',
  'initial_balance',
])

function humanizeKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return 'N/A'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return value.toString()
  return String(value)
}

interface ConfigSectionProps {
  title: string
  config: Record<string, unknown>
}

function ConfigSection({ title, config }: ConfigSectionProps) {
  const entries = Object.entries(config)
  if (entries.length === 0) return null

  return (
    <div className="@container">
      <h4 className="text-label font-semibold text-foreground mb-2">{title}</h4>
      <div className="grid grid-cols-1 @md:grid-cols-2 gap-x-6 gap-y-1.5">
        {entries.map(([key, value]) => {
          if (
            typeof value === 'object' &&
            value !== null &&
            !Array.isArray(value)
          ) {
            const subEntries = Object.entries(value as Record<string, unknown>)
            return (
              <div key={key} className="col-span-full">
                <div className="text-caption font-medium text-muted-foreground mt-2 mb-1">
                  {humanizeKey(key)}
                </div>
                <div className="ml-3 grid grid-cols-1 @md:grid-cols-2 gap-x-6 gap-y-1.5">
                  {subEntries.map(([subKey, subValue]) => (
                    <div
                      key={subKey}
                      className="flex items-center justify-between gap-3"
                    >
                      <span className="text-caption text-muted-foreground">
                        {humanizeKey(subKey)}
                      </span>
                      <span className="text-caption font-medium text-foreground text-right">
                        {renderValue(subValue)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          }

          return (
            <div
              key={key}
              className="flex items-center justify-between gap-3"
            >
              <span className="text-caption text-muted-foreground">
                {humanizeKey(key)}
              </span>
              <span className="text-caption font-medium text-foreground text-right">
                {renderValue(value)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function BacktestConfigGroups({
  configInput,
  className,
}: BacktestConfigGroupsProps) {
  const strategy = configInput?.strategy_config
  const risk = configInput?.risk_management_config
  const backtest = configInput?.backtest_config

  const filteredBacktest = backtest
    ? Object.fromEntries(
        Object.entries(backtest).filter(
          ([k]) => !BACKTEST_CONFIG_DEDUP_KEYS.has(k),
        ),
      )
    : null

  const hasStrategy = strategy && Object.keys(strategy).length > 0
  const hasRisk = risk && Object.keys(risk).length > 0
  const hasBacktest =
    filteredBacktest && Object.keys(filteredBacktest).length > 0

  if (!hasStrategy && !hasRisk && !hasBacktest) return null

  return (
    <div className={className}>
      <div className="flex flex-col gap-3">
        {hasStrategy && (
          <ConfigSection title="Strategy Parameters" config={strategy!} />
        )}
        {hasRisk && (
          <ConfigSection title="Risk Management" config={risk!} />
        )}
        {hasBacktest && (
          <ConfigSection title="Backtest Settings" config={filteredBacktest!} />
        )}
      </div>
    </div>
  )
}
